import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import {
  AppShell,
  Avatar,
  CAMPAIGN_WORDS,
  closingScreenShows,
  closingSeen,
  closingSeenKey,
  DiscardChangesDialog,
  freshState,
  KiteMark,
  ModeSwitch,
  markClosingSeen,
  modesOf,
  refusedRedirect,
  rememberPage,
  roleNames,
  Sidebar,
  SidebarItem,
  SidebarSection,
  switchTarget,
  useTheme,
} from "@vd/ui";
import {
  BadgeCheck,
  Blocks,
  ChartColumn,
  ChartNoAxesCombined,
  Database,
  FileSpreadsheet,
  FolderKanban,
  Hash,
  House,
  LifeBuoy,
  ListChecks,
  LogOut,
  MailCheck,
  MessagesSquare,
  Moon,
  Phone,
  PhoneForwarded,
  PhoneOutgoing,
  ScrollText,
  Siren,
  SlidersHorizontal,
  Sparkles,
  Sun,
  Users,
  Voicemail,
} from "lucide-react";
import { type ComponentType, useEffect, useState } from "react";
import { auth, meQuery, qk } from "../api/admin";
import { ApiError, setCsrfToken, setUnauthorizedHandler } from "../api/client";
import { closureKeys } from "../api/closure";
import { costKeys } from "../api/costs";
import { companyHasEmailMarketing } from "../api/sending";
import type { Me } from "../api/types";
import { liveSocket } from "../api/ws";
import { isAdmin as isAdminOf, managesReps, useMe } from "../auth/useMe";
import { AdminStrip } from "../components/closure/AdminStrip";
import { ConsoleClosingScreen } from "../components/closure/ConsoleClosingScreen";
import { ConsoleBell } from "../components/notifications/ConsoleBell";
import { Button } from "../components/ui";
import { RouteCrumbs } from "../lib/crumbs";
import { consoleSwitchModes, isAppPath, leaveFor, litItem } from "../lib/modeSwitch";
import { useNavigate } from "../lib/navigate";
import { setOwner, useBackGestures, useLevels } from "../lib/navTrail";
import { MemberPhotos } from "../lib/photos";

export const Route = createFileRoute("/admin")({
  beforeLoad: async ({ context, location }) => {
    let me: Me;
    try {
      me = await context.queryClient.ensureQueryData(meQuery);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        throw redirect({ to: "/signin", search: { redirect: location.href } });
      }
      throw e;
    }
    // Managers keep their pages here until App mode has them (story CA25). Anyone else without
    // Admin opens their own mode with a toast (spec 2.4).
    // Only once App mode is served (`Me.web_app`); until then the page says the console is for
    // admins and managers, as it always has.
    if (!managesReps(me) && me.web_app === true) {
      const refused = refusedRedirect(location.pathname, modesOf(me));
      if (refused) await leaveFor(refused.to, refused.toast);
    }
  },
  component: AppLayout,
});

type NavTo =
  | "/admin"
  | "/admin/live-now"
  | "/admin/calls"
  | "/admin/messages"
  | "/admin/people"
  | "/admin/campaigns"
  | "/admin/ai"
  | "/admin/ai/imports"
  | "/admin/numbers"
  | "/admin/phone-system/911"
  | "/admin/phone-system/voicemail"
  | "/admin/integrations"
  | "/admin/email-marketing"
  | "/admin/calling/policies"
  | "/admin/calling/dispositions"
  | "/admin/calling/parallel-dial"
  | "/admin/calling/voicemail-drops"
  | "/admin/billing/usage"
  | "/admin/billing"
  | "/admin/data"
  | "/admin/rep-analytics"
  | "/admin/audit"
  | "/admin/support";

export interface NavItem {
  to: NavTo;
  label: string;
  icon: ComponentType<{ className?: string }>;
  adminOnly?: boolean;
  /** Shown only when the company holds email marketing on some seat. */
  emailMarketing?: boolean;
}

export interface NavSection {
  /** The small heading over the section; none for Home. */
  label?: string;
  items: NavItem[];
}

/**
 * Admin's sidebar, in sections (DESIGN.md "Admin sidebar", approved 2026-11-01). Kite's own
 * pages (Live log, Costs, carrier tuning, Releases, Demo requests) are on the Platform console
 * only.
 */
export const NAV: NavSection[] = [
  { items: [{ to: "/admin", label: "Home", icon: House, adminOnly: true }] },
  {
    label: "Activity",
    items: [
      { to: "/admin/calls", label: "Calls", icon: Phone },
      { to: "/admin/messages", label: "Messages", icon: MessagesSquare },
      { to: "/admin/campaigns", label: CAMPAIGN_WORDS.screen, icon: FolderKanban },
      { to: "/admin/rep-analytics", label: "Rep analytics", icon: ChartNoAxesCombined },
    ],
  },
  {
    label: "Team",
    items: [
      { to: "/admin/people", label: "People", icon: Users },
      { to: "/admin/numbers", label: "Numbers", icon: Hash },
      // Managers read 911 in DESIGN.md, but the 911 routes answer Admins only (TC12, TC14).
      { to: "/admin/phone-system/911", label: "911", icon: Siren, adminOnly: true },
      { to: "/admin/phone-system/voicemail", label: "Voicemail", icon: Voicemail, adminOnly: true },
    ],
  },
  {
    label: "Calling",
    items: [
      { to: "/admin/calling/policies", label: "Policies", icon: SlidersHorizontal, adminOnly: true },
      // The company's own Parallel Dial settings; the carrier tuning is on Platform.
      {
        to: "/admin/calling/parallel-dial",
        label: "Parallel dialing",
        icon: PhoneForwarded,
        adminOnly: true,
      },
      {
        to: "/admin/calling/voicemail-drops",
        label: "Voicemail drops",
        icon: PhoneOutgoing,
        adminOnly: true,
      },
      // Admins and managers keep the dispositions (ADR 0015).
      { to: "/admin/calling/dispositions", label: "Dispositions", icon: ListChecks },
    ],
  },
  {
    label: "AI",
    items: [
      { to: "/admin/ai", label: "Assistant", icon: Sparkles, adminOnly: true },
      { to: "/admin/ai/imports", label: "Imports", icon: FileSpreadsheet, adminOnly: true },
    ],
  },
  {
    label: "Company",
    items: [
      { to: "/admin/integrations", label: "Integrations", icon: Blocks, adminOnly: true },
      // Policies > Email marketing, its own item beside Integrations (story EM29).
      {
        to: "/admin/email-marketing",
        label: "Email marketing",
        icon: MailCheck,
        adminOnly: true,
        emailMarketing: true,
      },
      { to: "/admin/billing", label: "Plan and seats", icon: BadgeCheck, adminOnly: true },
      { to: "/admin/billing/usage", label: "Usage", icon: ChartColumn, adminOnly: true },
      { to: "/admin/data", label: "Data", icon: Database, adminOnly: true },
      { to: "/admin/audit", label: "Audit log", icon: ScrollText, adminOnly: true },
      { to: "/admin/support", label: "Support", icon: LifeBuoy, adminOnly: true },
    ],
  },
];

/** The sections this person sees: their items only, and no section left empty. */
export function visibleNav(me: Pick<Me, "roles" | "seat">): NavSection[] {
  const admin = isAdminOf(me);
  const marketing = companyHasEmailMarketing(me);
  const shown = (n: NavItem) => (admin || !n.adminOnly) && (marketing || !n.emailMarketing);
  return NAV.map((s) => ({ ...s, items: s.items.filter(shown) })).filter((s) => s.items.length > 0);
}

function useRealtime(enabled: boolean) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    if (!enabled) return;
    let listTimer: ReturnType<typeof setTimeout> | null = null;
    const offFrame = liveSocket.onFrame((f) => {
      if (f.type === "call.updated" || f.type === "routing.updated" || f.type === "summary.ready") {
        const d = f.data as { id?: string; call_id?: string } | undefined;
        const id = d?.id ?? d?.call_id;
        if (id) qc.invalidateQueries({ queryKey: qk.call(id) });
        listTimer ??= setTimeout(() => {
          listTimer = null;
          qc.invalidateQueries({ queryKey: ["admin", "calls"] });
          qc.invalidateQueries({ queryKey: qk.overview });
        }, 3000);
      } else if (f.type === "review.updated") {
        // Ids only: refetch the rep's reviews and that review.
        const d = f.data as { id?: string; rep_user_id?: string } | undefined;
        if (d?.rep_user_id) qc.invalidateQueries({ queryKey: ["admin", "reviews", "list", d.rep_user_id] });
        if (d?.id) qc.invalidateQueries({ queryKey: ["admin", "reviews", "detail", d.id] });
        qc.invalidateQueries({ queryKey: ["reviews", "latest"] });
      } else if (f.type === "notification.created" || f.type === "notification.read") {
        qc.invalidateQueries({ queryKey: ["notifications"] });
        qc.invalidateQueries({ queryKey: ["reviews", "latest"] });
      } else if (f.type === "approvals.updated") {
        qc.invalidateQueries({ queryKey: ["notifications"] });
      } else if (f.type === "cost.alert") {
        qc.invalidateQueries({ queryKey: ["admin", "cost-alerts"] });
        qc.invalidateQueries({ queryKey: costKeys.alerts });
      } else if (f.type === "company.closure_changed") {
        // The account started or stopped closing: `/v1/me` carries the strip and the screen.
        qc.invalidateQueries({ queryKey: qk.me });
        qc.invalidateQueries({ queryKey: closureKeys.closure });
      } else if (f.type === "session.revoked") {
        liveSocket.stop();
        qc.clear();
        navigate({
          to: "/signin",
          search: { error: "session_signed_out" },
        });
      }
    });
    const offReconnect = liveSocket.onReconnect(() => {
      qc.invalidateQueries({ queryKey: ["admin"] });
    });
    liveSocket.start();
    return () => {
      offFrame();
      offReconnect();
      if (listTimer) clearTimeout(listTimer);
      liveSocket.stop();
    };
  }, [enabled, qc, navigate]);
}

function AppLayout() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const here = useRouterState({ select: (s) => s.location.href });
  // Where this tab was in Admin mode, for the switch's way back (spec 2.6).
  useEffect(() => rememberPage("admin", here), [here]);
  const switchModes = consoleSwitchModes(me);
  // The section where the trail started stays lit (ADR 0030).
  const trailTop = useLevels().above[0]?.href;
  const lit = trailTop ? (trailTop.split(/[?#]/)[0] ?? pathname) : pathname;
  const narrow = useNarrow();
  const { resolved, setTheme } = useTheme();
  useRealtime(managesReps(me));
  // Esc on a nested page, Alt+Left and the mouse's Back button (DESIGN.md, Navigation).
  useBackGestures();
  // Origins are this account's: none from before a sign-out, or from another account.
  const account = `${me.tenant.id}:${me.user.id}`;
  useEffect(() => {
    setOwner(account);
    return () => setOwner(null);
  }, [account]);
  // The closing screen, once per sign-in while the account is closing (ADR 0062). A closure that
  // opens while the app is open (an admin closed it on Admin > Data) gets the strip, never the
  // screen: it counts as seen.
  const closure = me.closure ?? null;
  const closingKey = closure ? closingSeenKey(me.tenant.id, me.user.id, closure.purge_after) : null;
  const [signInKey] = useState(closingKey);
  const [, setContinued] = useState(0);
  useEffect(() => {
    if (closingKey && closingKey !== signInKey) markClosingSeen(closingKey);
  }, [closingKey, signInKey]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      qc.removeQueries({ queryKey: qk.me });
      navigate({
        to: "/signin",
        search: { redirect: window.location.pathname + window.location.search },
      });
    });
    return () => setUnauthorizedHandler(null);
  }, [qc, navigate]);

  const signOut = async () => {
    try {
      await auth.logout();
    } catch {
      // Sign out locally either way.
    }
    setCsrfToken(null);
    liveSocket.stop();
    qc.clear();
    navigate({ to: "/signin" });
  };

  if (!managesReps(me)) {
    // The layout's guard has already sent this member to App mode.
    return (
      <main className="flex h-full items-center justify-center p-8 text-center">
        <div>
          <p className="text-body font-medium">That page is for admins.</p>
          <Button className="mt-4" size="sm" onClick={signOut}>
            Sign out
          </Button>
        </div>
      </main>
    );
  }
  if (closingScreenShows(closure) && closingKey && closingKey === signInKey && !closingSeen(closingKey)) {
    return (
      <ConsoleClosingScreen
        me={me}
        closure={closure}
        onSignOut={signOut}
        onContinue={(to) => {
          markClosingSeen(closingKey);
          setContinued((n) => n + 1);
          if (to) void navigate({ to });
        }}
      />
    );
  }
  const sections = visibleNav(me);
  const litTo = litItem(
    lit.replace(/\/$/, "") === "/admin/live-now" ? "/admin/calls" : lit,
    sections.flatMap((s) => s.items.map((n) => n.to)),
  );

  const go = (e: React.MouseEvent) => {
    // Sidebar items are real links; plain left clicks route in-app.
    const a = (e.target as Element).closest("a");
    if (!a || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const href = a.getAttribute("href");
    // App mode is another app on this host (the mode switch's App link): the browser loads it.
    if (!href?.startsWith("/") || isAppPath(href)) return;
    e.preventDefault();
    // A fresh start: the page offers no "Back to", even within its section.
    navigate({ href, state: freshState() });
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: delegates clicks on the links inside
    // biome-ignore lint/a11y/useKeyWithClickEvents: links keep their own keyboard behavior
    <div className="h-full" onClick={go}>
      <MemberPhotos me={me}>
        <AppShell
          sidebar={
            <Sidebar
              collapsed={narrow}
              header={
                <div className={narrow ? "flex flex-col items-center gap-2" : "flex flex-col gap-2.5"}>
                  <div
                    className={
                      narrow ? "flex flex-col items-center gap-2" : "flex h-8 items-center gap-2.5 px-1.5"
                    }
                  >
                    <KiteMark size={20} tone="on-dark" />
                    {narrow ? null : (
                      <span className="min-w-0 flex-1 truncate font-medium text-ui">{me.tenant.name}</span>
                    )}
                    <ConsoleBell compact={narrow} />
                  </div>
                  {/* App mode and Platform are other apps: their links load them. */}
                  <ModeSwitch
                    current="admin"
                    modes={switchModes}
                    collapsed={narrow}
                    onSwitch={() => false}
                    hrefFor={(m) =>
                      switchTarget(m, {
                        current: "admin",
                        here,
                        platformUrl: me.platform_url,
                        tenantId: me.tenant.id,
                        origin: window.location.origin,
                      })
                    }
                  />
                </div>
              }
              footer={
                <>
                  {narrow ? null : (
                    <div className="flex items-center gap-2.5 px-2.5 pt-1 pb-2">
                      <Avatar name={me.user.name} userId={me.user.id} size="md" aria-hidden />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-ui">{me.user.name}</p>
                        <p className="truncate text-caption text-ink-3">{roleLine(me)}</p>
                      </div>
                    </div>
                  )}
                  <SidebarItem
                    icon={resolved === "dark" ? <Sun /> : <Moon />}
                    label={resolved === "dark" ? "Light theme" : "Dark theme"}
                    onClick={() => setTheme(resolved === "dark" ? "light" : "dark")}
                  />
                  <SidebarItem icon={<LogOut />} label="Sign out" onClick={signOut} />
                </>
              }
            >
              {sections.map((sec) => (
                <SidebarSection key={sec.label ?? "home"} label={sec.label}>
                  {sec.items.map((n) => {
                    const Icon = n.icon;
                    return (
                      <SidebarItem
                        key={n.to}
                        href={n.to}
                        icon={<Icon />}
                        label={n.label}
                        active={n.to === litTo}
                      />
                    );
                  })}
                </SidebarSection>
              ))}
            </Sidebar>
          }
        >
          {/* One strip over every Admin page, admins only: a closing account's wins over Billing's
              (lib/adminStrip.ts). Billing's strip goes in `billing`. */}
          <AdminStrip me={me} />
          <div className="max-w-[1280px] px-4 py-5 sm:px-6 sm:py-6">
            <RouteCrumbs />
            <Outlet />
            {/* "Discard changes?" before going back from unsaved edits. */}
            <DiscardChangesDialog />
          </div>
        </AppShell>
      </MemberPhotos>
    </div>
  );
}

function roleLine(me: Me): string {
  return roleNames(me.roles).join(", ");
}

function useNarrow(query = "(max-width: 1023px)"): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof window !== "undefined" && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, [query]);
  return narrow;
}
